class CollectionSubcollectionsSlider extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.pagination = null;
    this.navigation = null;
    this.previousButton = null;
    this.nextButton = null;
    this.dots = [];
    this.frame = null;
    this.resizeObserver = null;

    this.onScroll = this.onScroll.bind(this);
    this.onPaginationClick = this.onPaginationClick.bind(this);
    this.onNavigationClick = this.onNavigationClick.bind(this);
    this.refresh = this.refresh.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-subcollections-track]');
    this.pagination = this.querySelector('[data-subcollections-pagination]');
    this.navigation = this.querySelector('[data-subcollections-navigation]');
    this.previousButton = this.querySelector('[data-subcollections-previous]');
    this.nextButton = this.querySelector('[data-subcollections-next]');
    if (!this.track || !this.pagination) return;

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.pagination.addEventListener('click', this.onPaginationClick);
    this.navigation?.addEventListener('click', this.onNavigationClick);
    this.resizeObserver = new ResizeObserver(this.refresh);
    this.resizeObserver.observe(this.track);
    this.refresh();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.pagination?.removeEventListener('click', this.onPaginationClick);
    this.navigation?.removeEventListener('click', this.onNavigationClick);
    this.resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.frame);
  }

  refresh() {
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 1;
    const pageCount = isMobile && this.track.clientWidth
      ? Math.ceil(this.track.scrollWidth / this.track.clientWidth)
      : 1;

    if (pageCount <= 1) {
      this.pagination.hidden = true;
      this.pagination.replaceChildren();
      this.dots = [];
    } else {
      if (this.dots.length !== pageCount) {
        const fragment = document.createDocumentFragment();
        this.dots = Array.from({ length: pageCount }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'collection-subcollections__pagination-dot';
          dot.dataset.index = index;
          dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${pageCount}`);
          fragment.appendChild(dot);
          return dot;
        });
        this.pagination.replaceChildren(fragment);
      }

      this.pagination.hidden = false;
    }

    if (this.navigation) this.navigation.hidden = isMobile || !hasOverflow;
    this.updateActiveDot();
    this.updateNavigation();
  }

  onScroll() {
    window.cancelAnimationFrame(this.frame);
    this.frame = window.requestAnimationFrame(() => {
      this.updateActiveDot();
      this.updateNavigation();
    });
  }

  onPaginationClick(event) {
    const dot = event.target.closest('.collection-subcollections__pagination-dot');
    if (!dot || !this.pagination.contains(dot)) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const denominator = Math.max(this.dots.length - 1, 1);
    const target = maxScroll * (Number(dot.dataset.index) / denominator);
    this.track.scrollTo({ left: target, behavior: 'smooth' });
  }

  onNavigationClick(event) {
    const previous = event.target.closest('[data-subcollections-previous]');
    const next = event.target.closest('[data-subcollections-next]');
    if (!previous && !next) return;

    const direction = previous ? -1 : 1;
    this.track.scrollBy({ left: this.track.clientWidth * 0.8 * direction, behavior: 'smooth' });
  }

  updateNavigation() {
    if (!this.previousButton || !this.nextButton) return;

    const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    this.previousButton.disabled = this.track.scrollLeft <= 1;
    this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
  }

  updateActiveDot() {
    if (!this.dots.length) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const progress = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 0;
    const activeIndex = Math.round(progress * (this.dots.length - 1));
    const thumbWidth = maxScroll > 0 ? Math.max(15, (this.track.clientWidth / this.track.scrollWidth) * 100) : 100;

    this.pagination.style.setProperty('--mobile-pagination-thumb-width', `${thumbWidth}%`);
    this.pagination.style.setProperty('--mobile-pagination-thumb-left', `${progress * (100 - thumbWidth)}%`);

    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);

      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('collection-subcollections-slider')) {
  customElements.define('collection-subcollections-slider', CollectionSubcollectionsSlider);
}

window.wetheme?.webcomponentRegistry?.register({ key: 'collection-subcollections-slider' });
